import { exitSudo } from 'API/account';
import { getAvailableProducts, getFullLicense } from 'API/license';
import { getUserInfo } from 'API/user';
import { Products } from 'enums';
import i18n from 'i18n';
import jwt from 'jsonwebtoken';
import { useState } from 'react';
import useUserStore from 'stores/user-store';
import { UserInfo } from 'types/user.types';
import { useExtensionAuth } from './use-extension-auth';

/**
 * Ends an admin impersonation session and restores the admin's own session.
 *
 * Starting an impersonation overwrites the admin's session, so there is nothing
 * stored client-side to switch back to. The server rebuilds the admin session from
 * the `sudoAdmin` claim in the current token instead — no password prompt.
 *
 * Returns false when the exchange fails (e.g. the impersonation token has expired).
 * Callers should fall back to the password dialog in that case.
 */
export const useExitSudo = () => {
  const { extensionSignIn } = useExtensionAuth();
  const signIn = useUserStore((state) => state.signIn);
  const setToken = useUserStore((state) => state.setToken);
  const setLicenses = useUserStore((state) => state.setLicenses);

  const [loading, setLoading] = useState(false);

  const clearImpersonationMarkers = () => {
    localStorage.removeItem('adminEmail');
    // Nothing writes adminLoginTime any more; cleared for browsers that still carry
    // one from before the countdown was removed.
    localStorage.removeItem('adminLoginTime');
    localStorage.removeItem('rememberLinkedinAccount');
  };

  const exitImpersonation = async (): Promise<boolean> => {
    // Read before the exchange — setToken replaces it with the admin session.
    const impersonationRefreshToken = useUserStore.getState().token?.refreshToken;

    setLoading(true);

    try {
      // Hand over the impersonation refresh token so the server can revoke it —
      // otherwise the impersonated session would stay resumable after leaving.
      const res = await exitSudo(impersonationRefreshToken);

      if (res.error || !res.data) {
        return false;
      }

      // Apply the admin token before the follow-up calls so they run as the admin.
      setToken(res.data.token);

      const [userInfoRes, productsRes, licensesRes] = await Promise.all([
        getUserInfo(),
        getAvailableProducts(),
        getFullLicense(),
      ]);

      if (userInfoRes.error) {
        return false;
      }

      const userInfo: UserInfo = new UserInfo(userInfoRes);
      const hasLicenseError = !Array.isArray(licensesRes) && 'error' in licensesRes;
      if (!hasLicenseError) {
        setLicenses(Array.isArray(licensesRes) ? licensesRes : []);
      }

      if (!productsRes.error) {
        signIn(res.data.user, userInfo, productsRes.products, res.data.token);
      } else {
        // Products endpoint failed — fall back to the licenses carried in the token.
        const decoded: any = jwt.decode(res.data.token.accessToken, { complete: true });
        const products: Products[] = decoded?.payload?.possessedLicenses ?? [];
        signIn(res.data.user, userInfo, products, res.data.token);
      }

      i18n.changeLanguage(res.data.user.language);
      extensionSignIn(res.data.token, res.data.user);

      clearImpersonationMarkers();
      return true;
    } finally {
      setLoading(false);
    }
  };

  return { exitImpersonation, exitSudoLoading: loading };
};
